import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { AgentsService } from '@generated/services/agents.service';
import { TestsService } from '@generated/services/tests.service';
import { AgentSummary } from '@generated/models/agent-summary';
import { RunTestsResult } from '@generated/models/run-tests-result';
import { NotificationService } from '@core/services/notification.service';
import { RUN_TEST_FIELDS } from '@core/assistant/assistant-catalog';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';

@Component({
  selector: 'app-run-test-drawer',
  standalone: true,
  imports: [
    FormsModule,
    DrawerModule,
    SelectModule,
    ButtonModule,
    CheckboxModule,
    MessageModule,
    ProgressSpinnerModule,
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective
],
  templateUrl: './run-test-drawer.component.html',
  styleUrl: './run-test-drawer.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RunTestDrawerComponent {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = RUN_TEST_FIELDS;
  private agentsService = inject(AgentsService);
  private testsService = inject(TestsService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  /** Two-way visibility. */
  readonly visible = model<boolean>(false);
  /** Test being launched. */
  readonly testId = input<string | null>(null);
  readonly testName = input<string>('');

  /** Emitted after a successful run with the API result. */
  readonly testRunStarted = output<RunTestsResult>();

  readonly agents = signal<AgentSummary[]>([]);
  readonly agentsLoading = signal<boolean>(false);
  readonly selectedAgentId = signal<string | null>(null);
  readonly attachToConfig = signal<boolean>(true);
  readonly isRunning = signal<boolean>(false);

  private agentsLoaded = false;

  readonly agentOptions = computed(() => {
    const unnamed = $localize`:@@runTest.unnamedAgent:Unnamed agent`;
    return this.agents()
      .map(a => ({ label: a.title || unnamed, value: a.id || '' }))
      .filter(o => o.value);
  });

  readonly subtitle = computed(() =>
    $localize`:@@runTest.subtitle:Select an agent to run the test "${this.testName()}:name:"`,
  );

  constructor() {
    // Load agents lazily the first time the drawer opens; reset form on close.
    effect(() => {
      if (this.visible()) {
        if (!this.agentsLoaded) this.loadAgents();
      } else {
        this.selectedAgentId.set(null);
        this.attachToConfig.set(true);
        this.isRunning.set(false);
      }
    });
  }

  private loadAgents(): void {
    this.agentsLoading.set(true);
    this.agentsService.apiAgentsGet().subscribe({
      next: (result) => {
        this.agents.set(result.agents || []);
        this.agentsLoaded = true;
        this.agentsLoading.set(false);
      },
      error: () => {
        this.notificationService.showError($localize`:@@runTest.notify.agentsFail:Failed to load agents`);
        this.agentsLoading.set(false);
      },
    });
  }

  cancel(): void {
    this.visible.set(false);
  }

  run(): void {
    const agentId = this.selectedAgentId();
    const testId = this.testId();
    if (!agentId || !testId) return;

    this.isRunning.set(true);

    const start = () => {
      this.testsService.apiTestRunsPost({ body: { agent_id: agentId, test_ids: [testId] } }).subscribe({
        next: (result) => {
          this.isRunning.set(false);
          const failed = result.failures?.length ?? 0;
          if (failed > 0) {
            this.notificationService.showWarning(
              $localize`:@@runTest.notify.partial:Test run started with ${failed}:count: failure(s)`,
            );
          } else {
            this.notificationService.showSuccess($localize`:@@runTest.notify.started:Test run started`);
          }
          this.testRunStarted.emit(result);
          this.visible.set(false);
          this.router.navigate(['/tests/runs']);
        },
        error: () => {
          this.isRunning.set(false);
          this.notificationService.showError($localize`:@@runTest.notify.runFail:Failed to start test run`);
        },
      });
    };

    if (this.attachToConfig()) {
      // POST replaces the whole set, so fetch the current attachments and
      // append this test (skip if already present), then run.
      this.testsService.apiAgentsAgentIdTestsGet({ agentId }).subscribe({
        next: (res) => {
          const current = (res.agent_tests ?? [])
            .map(at => at.test_id)
            .filter((id): id is string => !!id);
          if (current.includes(testId)) {
            start();
            return;
          }
          this.testsService.apiAgentsAgentIdTestsPost({
            agentId,
            body: { test_ids: [...current, testId] },
          }).subscribe({
            next: () => start(),
            error: () => {
              this.isRunning.set(false);
              this.notificationService.showError($localize`:@@runTest.notify.attachFail:Failed to attach test to the agent`);
            },
          });
        },
        error: () => {
          this.isRunning.set(false);
          this.notificationService.showError($localize`:@@runTest.notify.attachFail:Failed to attach test to the agent`);
        },
      });
    } else {
      start();
    }
  }
}
